import type {
  CancellationRowDto,
  DocumentPassengerDto,
  InvoiceHotelDto,
  InvoiceTransportDto,
  IsoDate,
  Money,
  PassengerDto,
  VoucherWithInvoiceDto,
} from '@gty/common';

/**
 * WHAT A VOUCHER PRINTS, AND WHERE EACH BLOCK COMES FROM.
 *
 * A voucher is a TRAVEL document, not a financial one. It is raised by the
 * token payment — the moment the seat is confirmed — which is before any
 * invoice exists, and its job from that moment is to tell the traveller where
 * to be, when, with whom, and what is included. None of that needs an invoice
 * total, so the voucher renders in full whether or not one exists:
 *
 *  - Guest and tour            are the VOUCHER'S OWN snapshot, taken from the
 *                              booking, its customer and its tour when the
 *                              voucher was raised — see `resolveVoucherContent`
 *                              below. Falls back to the invoice's copy, and
 *                              then to a `booking` prop, only for a voucher
 *                              raised before that snapshot existed.
 *  - Passengers                come from the VOUCHER'S OWN rows, for the same
 *                              reason and by the same mechanism — see
 *                              `resolvePassengers`, which predates the guest/
 *                              tour snapshot but follows the identical pattern.
 *  - Hotels, transport, notes,
 *    cancellation              are invoice SNAPSHOTS. Absent, they render their
 *                              existing empty states ("No accommodation
 *                              recorded"), which is honest — they have not been
 *                              confirmed yet, and printing nothing is right.
 *  - Payment Summary           is REPLACED, not blanked, when there is no
 *                              invoice. A travel document must never print ₹0
 *                              next to "Total Amount" — a reader would take
 *                              that as the price. It prints the booking
 *                              reference and the balance due date instead, and
 *                              says plainly that the tax invoice follows on
 *                              full payment.
 *
 * The alternative — hiding the voucher until an invoice exists — was rejected:
 * that is exactly the state the whole token→EMI flow spends most of its life in,
 * and the traveller needs the document during it.
 *
 * GUEST/TOUR RESOLUTION ORDER: voucher's own field → invoice's → `booking` prop
 * → em dash, decided PER FIELD in ONE place (`fromVoucher` below), so no
 * section of the printed sheet can pick a different fallback from another. Only
 * a voucher raised before the guest/tour snapshot shipped ever reaches past the
 * first step — every voucher raised since carries its own copy of all of it.
 */

/**
 * The booking fields a voucher can borrow when no invoice has been snapshotted.
 *
 * A structural subset of `BookingDto` rather than the whole thing: the document
 * has no business reading a booking's payment state or activity trail, and
 * naming only what it uses keeps that true.
 */
export interface VoucherBookingContext {
  ref: string;
  customerName: string;
  tourName: string;
  departureDate: IsoDate;
  returnDate: IsoDate;
  pax: number;
  passengers: readonly PassengerDto[];
}

/** Rendered as "—" wherever a value genuinely is not known yet. */
export const UNKNOWN = '—';

export interface VoucherMoney {
  packageCost: Money;
  extraCharges: Money;
  discount: Money;
  gstRate: number;
  gstAmount: Money;
  total: Money;
  advanceReceived: Money;
  /** `total - advanceReceived`, computed once here rather than at each caller. */
  balance: Money;
}

export interface VoucherPrintModel {
  bookingRef: string;
  /** Null before an invoice fixes the sale date. */
  bookingDate: IsoDate | null;

  guestName: string;
  guestPhone: string;
  guestEmail: string;
  guestAddress: string;

  packageName: string;
  travelDate: IsoDate | null;
  returnDate: IsoDate | null;
  duration: string;
  departureCity: string;
  destination: string;

  passengers: readonly DocumentPassengerDto[];
  hotels: readonly InvoiceHotelDto[];
  transport: readonly InvoiceTransportDto[];
  notes: readonly string[];
  cancellation: readonly CancellationRowDto[];

  /**
   * NULL AT TOKEN STAGE, and that is the whole point.
   *
   * A consumer must branch on it rather than defaulting the figures to zero:
   * "₹0 total" on a printed voucher is a wrong price, not a missing one.
   */
  money: VoucherMoney | null;
}

/**
 * A booking passenger widened to a document's printed shape.
 *
 * Gender and ID TYPE are captured on a DOCUMENT for statutory reasons and are
 * simply not on a booking, so they stay unstated rather than being guessed — a
 * voucher that invents "Male" against a name is worse than one that admits it
 * does not know.
 *
 * `gender` used to be forced to `'Other'` here, because the DTO's union had no
 * "unknown" member and only the renderer could blank it. The column and the DTO
 * are both nullable now, so the absence is representable end to end and this
 * function no longer has to manufacture a placeholder for `printedGender` to
 * hide.
 */
function fromBookingPassenger(p: PassengerDto): DocumentPassengerDto {
  return {
    id: p.id,
    name: p.name,
    age: p.age,
    gender: null,
    idType: '',
    idNumber: p.idNumber,
  };
}

/**
 * Gender AS PRINTED. An em dash when nobody has stated it.
 *
 * Takes no "is this from an invoice?" flag any more: the value itself now
 * carries whether it is known, so a caller cannot get the answer wrong by
 * passing the wrong source. A row edited on the document prints what staff
 * chose, whichever document it came from.
 */
export function printedGender(p: DocumentPassengerDto): string {
  return p.gender ?? UNKNOWN;
}

/**
 * THE PASSENGER TABLE, RESOLVED SEPARATELY FROM EVERYTHING ELSE.
 *
 * The rest of the sheet is invoice-first because the invoice is the richer
 * snapshot. The passenger list is not, and cannot be: the voucher now owns its
 * own rows, and those are the ones staff correct on the voucher. Preferring the
 * invoice's copy here would mean a corrected voucher silently reverting to the
 * invoice's spelling the moment the booking was invoiced — which is the failure
 * this whole change exists to prevent.
 *
 *   voucher's own rows -> the editable, document-owned list
 *   invoice's rows     -> a voucher raised before this shipped whose booking has
 *                         no live passengers, but which was later invoiced
 *   booking's rows     -> the last resort, as before
 *
 * Falling back rather than requiring rows is deliberate: every pre-existing
 * voucher keeps printing exactly what it printed.
 */
function resolvePassengers(
  v: VoucherWithInvoiceDto,
  booking?: VoucherBookingContext,
): readonly DocumentPassengerDto[] {
  if (v.voucher.passengers.length > 0) return v.voucher.passengers;
  if (v.invoice !== null && v.invoice.passengers.length > 0) return v.invoice.passengers;
  return (booking?.passengers ?? []).map(fromBookingPassenger);
}

/**
 * One string field of the waterfall: the first source that has something to
 * say, left to right. Every source here defaults an unset field to `''`
 * (both `Voucher` and `Invoice` columns do), so `''` is treated as "not
 * stated" and skipped exactly like `null`/`undefined` — never printed as a
 * blank where the caller expects a value or an em dash.
 */
function pick(...values: ReadonlyArray<string | null | undefined>): string {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return UNKNOWN;
}

/** Same waterfall, for the two nullable travel dates. */
function pickDate(...values: ReadonlyArray<IsoDate | null | undefined>): IsoDate | null {
  for (const value of values) {
    if (value !== null && value !== undefined) return value;
  }
  return null;
}

export function resolveVoucherContent(
  v: VoucherWithInvoiceDto,
  booking?: VoucherBookingContext,
): VoucherPrintModel {
  const { voucher, invoice } = v;
  const passengers = resolvePassengers(v, booking);

  /**
   * THE GUEST/TOUR WATERFALL, DECIDED ONCE HERE — voucher's own snapshot
   * first, the invoice's copy next (a voucher raised before the snapshot
   * shipped has none of its own), a `booking` prop last (narrower still: it
   * carries no contact details at all, so `guestPhone`/`guestEmail` cannot
   * reach that far). Used by BOTH branches below, so a field can never read
   * differently depending on whether an invoice happens to exist.
   */
  const guestName = pick(voucher.guestName, invoice?.customerName, booking?.customerName);
  const guestPhone = pick(voucher.guestPhone, invoice?.customerPhone);
  const guestEmail = pick(voucher.guestEmail, invoice?.customerEmail);
  const guestAddress = pick(voucher.guestAddress, invoice?.customerAddress);
  const packageName = pick(voucher.packageName, invoice?.packageName, booking?.tourName);
  const travelDate = pickDate(voucher.travelDate, invoice?.travelDate, booking?.departureDate);
  const returnDate = pickDate(voucher.returnDate, invoice?.returnDate, booking?.returnDate);
  const duration = pick(voucher.duration, invoice?.duration);
  const departureCity = pick(voucher.departureCity, invoice?.departureCity);
  const destination = pick(voucher.destination, invoice?.destination);

  if (invoice !== null) {
    return {
      bookingRef: voucher.bookingRef,
      bookingDate: invoice.bookingDate,
      guestName,
      guestPhone,
      guestEmail,
      guestAddress,
      packageName,
      travelDate,
      returnDate,
      duration,
      departureCity,
      destination,
      passengers,
      hotels: invoice.hotels,
      transport: invoice.transport,
      notes: invoice.notes,
      cancellation: invoice.cancellation,
      money: {
        packageCost: invoice.packageCost,
        extraCharges: invoice.extraCharges,
        discount: invoice.discount,
        gstRate: invoice.gstRate,
        gstAmount: invoice.gstAmount,
        total: invoice.total,
        /**
         * The BOOKING's live `paid`, not the invoice's stored snapshot — the
         * server derives this per request (see `toInvoiceDto`, BUG 3). The
         * voucher reprints the invoice's money block, so it inherited the same
         * phantom balance and is fixed by the same single change.
         */
        advanceReceived: invoice.advanceReceived,
        // Clamped at 0 — same reasoning and same fix as `InvoicesPage.tsx`
        // (pre-merge finding N1, cheque-clearing pass): `advanceReceived`
        // stays live/unclamped on purpose (BUG 3), but a settlement that
        // pushes `paid` past `total` must not print a negative balance on a
        // reprinted voucher. The excess stays visible on the payment ledger.
        balance: Math.max(invoice.total - invoice.advanceReceived, 0),
      },
    };
  }

  return {
    bookingRef: voucher.bookingRef,
    bookingDate: null,
    guestName,
    guestPhone,
    guestEmail,
    guestAddress,
    packageName,
    travelDate,
    returnDate,
    duration,
    departureCity,
    destination,
    passengers,
    hotels: [],
    transport: [],
    notes: [],
    cancellation: [],
    money: null,
  };
}
